import React from 'react';

interface SectionTitleProps {
  title: string;
  subtitle?: string;
  text?: string;
  wordLimit?: number;
  showWordCount?: boolean;
  isWarning?: boolean;
  className?: string;
}

export const SectionTitle = React.memo(function SectionTitle({
  title,
  subtitle,
  text,
  wordLimit = 100,
  showWordCount = false,
  isWarning = false,
  className = '',
}: SectionTitleProps) {
  // Calculate word count - optimized
  const wordCount = React.useMemo(() => {
    if (!text || !showWordCount || !text.trim()) return 0;
    return text
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
  }, [text, showWordCount]);

  // Calculate word counter styling
  const getWordCountColor = React.useMemo(() => {
    if (wordCount < 50) return 'text-transparent'; // Hidden until 50 words
    if (wordCount < 85) return 'text-gray-400';
    if (wordCount < 95) return 'text-yellow-400';
    if (wordCount < wordLimit) return 'text-orange-400';
    return 'text-red-400';
  }, [wordCount, wordLimit]);

  const shouldShowCounter = showWordCount && wordCount >= 50;

  return (
    <div className={`mb-3 ${className}`}>
      {/* Title Row with Word Counter */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          {isWarning && <span className="text-[#ffcc00]">⚠️</span>}
          <h3 className="text-base font-bold bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] bg-clip-text text-transparent">
            {title}
          </h3>
        </div>

        {/* Word Counter */}
        {shouldShowCounter && (
          <div
            className={`flex-shrink-0 text-xs font-mono transition-all duration-300 ${getWordCountColor}`}
          >
            {wordCount >= wordLimit ? (
              <span className="text-red-400">
                {wordCount} / {wordLimit} words (Limit Reached)
              </span>
            ) : (
              <span>
                {wordCount} / {wordLimit} words
              </span>
            )}
          </div>
        )}
      </div>

      {/* Subtitle Row */}
      {subtitle && (
        <p
          className={`text-sm leading-relaxed transition-all duration-300 ${
            isWarning ? 'text-[#ffcc00] animate-pulse' : 'text-gray-300'
          }`}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
});
